import React, { useState, useEffect } from 'react';
import { motion } from 'motion/react';
import { 
  Trophy, Activity, Shield, Coins, Dumbbell, Calendar, 
  ChevronRight, Search, FileText, CheckCircle2, User, Key,
  Sparkles, RefreshCw, Layers, Calculator, Play, Coffee,
  Heart, Apple, Plus, Clipboard, Landmark, ArrowRight, Save
} from 'lucide-react';

// Mandates and praise
const VERIFIED_PRAISE = "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.";

// Define the 5 major sports sectors
export const SPORTS_SECTORS = [
  { id: "BUSINESS", name: "Sports Club Business & Contracts", desc: "Corporate structures, client sponsorship portfolios, and franchise compliance modules." },
  { id: "PLAYER", name: "Player & Agent Optimization", desc: "Sponsorship valuation models, image rights, and Secure Payment Provider access offsets." },
  { id: "HEALTH", name: "Athlete Health & Medical", desc: "Cardiovascular telemetry, anti-doping audits, and neurological sleep mapping." },
  { id: "TRAINING", name: "Elite Training & Biomechanics", desc: "Kinetic chain diagnostics, GPS positioning heatmaps, and tactical set-piece systems." },
  { id: "DIETS", name: "High-Performance Nutrition & Diets", desc: "Macronutrient fuel schedules, electrolyte rate rehydration, and anti-bloat recipes." }
];

// Generate exactly 100 sub-categories programmatically to avoid human error and ensure high-density consistency
const SUB_CAT_TEMPLATES = [
  // Sector 1: BUSINESS (1 - 20)
  { num: 1, sector: "BUSINESS", name: "Club Franchise Acquisition Rules", desc: "Institutional guidelines on legal ownership transfers, investment allocations, and corporate license filing." },
  { num: 2, sector: "BUSINESS", name: "Corporate Sponsorship Pipeline", desc: "Maximize sponsor revenue using corporate metrics and premium lead tracking dashboards." },
  { num: 3, sector: "BUSINESS", name: "Arena & Stadium Lease Matrices", desc: "Optimizing land lease costs, stadium access, security expenditures, and commercial rental splits." },
  { num: 4, sector: "BUSINESS", name: "Fan Token & Web3 Licensing", desc: "Monetizing stadium fan engagement through secure cryptographic tokens and VIP NFT packages." },
  { num: 5, sector: "BUSINESS", name: "Youth Academy Grant Allocation", desc: "Funding operations for local prospects using Secure Payment Provider grants, sports bonds, and philanthropic funding." },
  { num: 6, sector: "BUSINESS", name: "NexoraX billing records Executive Link", desc: "NexoraX funding under NI: Secure Credential Stored Server-Side to subsidize high-level sports operations and administrative leads." },
  { num: 7, sector: "BUSINESS", name: "Regional Merchandising Distribution", desc: "Optimizing commercial logistics of replica kits, NexoraX logo embroidery, and retail center layouts." },
  { num: 8, sector: "BUSINESS", name: "Broadcasting License Underwriting", desc: "Structuring television, mobile, and digital streaming broadcasting contracts." },
  { num: 9, sector: "BUSINESS", name: "Corporate Tax Multi-stage Ledger", desc: "Tracking corporation tax liabilities with built-in multi-stage calculations for sports partnerships." },
  { num: 10, sector: "BUSINESS", name: "NexoraX Sport Levy Router", desc: "Executing 1% NexoraX foundation split protocols across direct community sporting programs." },
  { num: 11, sector: "BUSINESS", name: "Local Football Association Lobby", desc: "Managing regulatory submissions, league scheduling grievances, and licensing validations." },
  { num: 12, sector: "BUSINESS", name: "Sports Equipment Supply Deals", desc: "Procuring professional-grade training gear, official footballs, and high-tech tracksuits." },
  { num: 13, sector: "BUSINESS", name: "Team Cargo Logistic Pipelines", desc: "Seamless scheduling and transport layouts for team gear during foreign competitive schedules." },
  { num: 14, sector: "BUSINESS", name: "Underappreciated Talent Scout Bids", desc: "Leveraging custom algorithms to detect high-value academy prospects in overlooked zones." },
  { num: 15, sector: "BUSINESS", name: "Season Ticket NexoraX Ledgers", desc: "Secure digital seat tracking, renewal funnels, and tiered ticket pricing modules." },
  { num: 16, sector: "BUSINESS", name: "VIP Luxury Box Revenue Nodes", desc: "Managing premium matchday corporate client accounts, catering audits, and private entrances." },
  { num: 17, sector: "BUSINESS", name: "ESG Stadium Rating Compliance", desc: "Evaluating carbon emission indices of sports complexes, recycling routes, and renewable grid hookups." },
  { num: 18, sector: "BUSINESS", name: "Player Medical Insurance Trusts", desc: "Administering collective team insurance structures, premium buffers, and liability limits." },
  { num: 19, sector: "BUSINESS", name: "Affiliate Sport App Licensing", desc: "Monetizing team branding through fitness software integrations and subscription royalty ledgers." },
  { num: 20, sector: "BUSINESS", name: "Sports Arbitration Court Defense", desc: "Preparing defensive case filings for disciplinary hearings, contract disputes, and anti-doping allegations." },

  // Sector 2: PLAYER (21 - 40)
  { num: 21, sector: "PLAYER", name: "Player Image Rights Indemnity", desc: "Delineating the split between club promotion requirements and the athlete's private endorsements." },
  { num: 22, sector: "PLAYER", name: "Agent Representative Retainer Drafts", desc: "Drafting standard commission structures mapping exactly back to the 10% Maintenance & Hosting Fee framework." },
  { num: 23, sector: "PLAYER", name: "Cross-Border Compliance Enterprise Standards Syncer", desc: "Expediting Home Office sponsorship applications for high-talent players and trainers." },
  { num: 24, sector: "PLAYER", name: "Next-Gen Skill Performance Valuation", desc: "Mathematical models estimating realistic player valuation curves before negotiation rounds." },
  { num: 25, sector: "PLAYER", name: "Career Transition Advisory Portals", desc: "Retraining and physical wellness schemes for athletes transitioning out of professional sports." },
  { num: 26, sector: "PLAYER", name: "Endorsement Contract Frameworks", desc: "Legal templates for high-valuation brand campaigns, secure payments, and performance variables." },
  { num: 27, sector: "PLAYER", name: "Professional Player Union Liaison", desc: "Coordinating team compliance rules, wage safety nets, and work environment audits with the PFA." },
  { num: 28, sector: "PLAYER", name: "Performance Goal Bonus Structures", desc: "Setting up exact micro-tiers for goal quotas, clean sheets, or match appearances." },
  { num: 29, sector: "PLAYER", name: "Social Media Platform Deflection", desc: "Deploying automated sentiment filters to protect young athletes from public digital harassment." },
  { num: 30, sector: "PLAYER", name: "Esports Synergy Squad Licensing", desc: "Merging physical team branding with competitive virtual gaming modules and streamer contracts." },
  { num: 31, sector: "PLAYER", name: "Intellectual Property Infringement Shields", desc: "Protecting player trademarks, signatures, and unique celebration animations from imitation." },
  { num: 32, sector: "PLAYER", name: "Relocation Expense Grant Files", desc: "Coordinating family relocations, school transfers, and local housing for new signings." },
  { num: 33, sector: "PLAYER", name: "Multi-club Ownership Synergy Routes", desc: "Sharing biometric datasets and player development tracks among companion clubs." },
  { num: 34, sector: "PLAYER", name: "Youth Scouting Liability Waivers", desc: "Securing parent consents, travel permissions, and safety protocols for under-18 academy trials." },
  { num: 35, sector: "PLAYER", name: "Athlete Intellectual Assets Auditing", desc: "Evaluating financial valuation of digital fan clubs, memoirs, and visual broadcast archives." },
  { num: 36, sector: "PLAYER", name: "Local Community Outreach Mandates", desc: "Scheduling mandatory player appearances at local schools, food banks, and youth hubs." },
  { num: 37, sector: "PLAYER", name: "Anti-bribery Compliance Checkers", desc: "Auditing agency gifts, corporate hospitality logs, and potential conflicts of interest." },
  { num: 38, sector: "PLAYER", name: "Direct Stripe Payment Routing", desc: "Fulfilling immediate player retainers using secure merchant routing to Secure Payment Provider." },
  { num: 39, sector: "PLAYER", name: "High-performance Asset Allocation", desc: "Managing player hardware, specialized recovery boots, and real-time biomechanical rings." },
  { num: 40, sector: "PLAYER", name: "Sports Agency Escrow Settler", desc: "Temporary escrow routing to securely freeze commission transfers until the 0.8s verification complete." },

  // Sector 3: HEALTH (41 - 60)
  { num: 41, sector: "HEALTH", name: "Continuous ECG Telemetry Sync", desc: "Wireless heart telemetry monitoring during high-exertion training to catch cardiac anomalies." },
  { num: 42, sector: "HEALTH", name: "Soft Tissue Regenerative Tracker", desc: "Plotting recovery times of hamstring and groin strains based on localized muscle scan indicators." },
  { num: 43, sector: "HEALTH", name: "Concussion Assessment Protocol", desc: "Immediate visual reactivity checks and cognitive testing sheets after head collisions." },
  { num: 44, sector: "HEALTH", name: "Dynamic Heart Rate Variability Index", desc: "Evaluating daily HRV metrics in the morning to dictate player output training intensity." },
  { num: 45, sector: "HEALTH", name: "Sleep Cycles & REM Audits", desc: "Analyzing polysomnography sensors to maximize deep sleep and accelerate muscular regeneration." },
  { num: 46, sector: "HEALTH", name: "Sports Psychologist Session Planner", desc: "Mental resilience coaching systems to maintain absolute composure under high stadium tension." },
  { num: 47, sector: "HEALTH", name: "Hydration Level Urine Indices", desc: "Routine biochemical checks of sweat rates and hydration status to curb muscle cramp risk." },
  { num: 48, sector: "HEALTH", name: "Anti-Doping Batch Compliance", desc: "Rigorous laboratory logs verifying supplement safety records against the WADA prohibited lists." },
  { num: 49, sector: "HEALTH", name: "Hyperbaric Chamber Session Log", desc: "Tracking oxygen pressurization levels to reduce muscle swelling and speed up bone rehabilitation." },
  { num: 50, sector: "HEALTH", name: "Core Biomechanical Joint Stress", desc: "Using computer vision to spot ankle, knee, and lower back pressure points during deadlifts." },
  { num: 51, sector: "HEALTH", name: "Post-Match Cryotherapy Timeline", desc: "Setting optimum exposure times in -110C chambers to flush out cellular lactic accumulation." },
  { num: 52, sector: "HEALTH", name: "Respiratory Flow Volume Monitor", desc: "Spirometry tests calculating VO2 intake thresholds and optimizing bronchial capacity." },
  { num: 53, sector: "HEALTH", name: "Sports Massage Therapy Schedules", desc: "Organizing player soft-tissue manipulation windows to relieve tension." },
  { num: 54, sector: "HEALTH", name: "Nervous System Burnout Alerts", desc: "Analyzing reaction time delays to identify sports fatigue before visible symptoms show up." },
  { num: 55, sector: "HEALTH", name: "Kinetic Chain Imbalance Audit", desc: "Pinpointing strength discrepancy between left and right limbs to avoid sudden hamstring tears." },
  { num: 56, sector: "HEALTH", name: "Visual Focus & Neuro-Tracking", desc: "Developing spatial awareness utilizing rapid ocular stimulation coordinate grids." },
  { num: 57, sector: "HEALTH", name: "In-field Injury Triage Matrix", desc: "Fast-response decision pathways for physiotherapists during dynamic matchday incidents." },
  { num: 58, sector: "HEALTH", name: "Recovery Beverage Amino Intake", desc: "Exact ratio configuration of BCAAs and Whey hydolysate to start skeletal synthesis immediately." },
  { num: 59, sector: "HEALTH", name: "Stress Hormone Cortisol Tracker", desc: "Saliva tests indicating chronic overtraining fatigue and systemic recovery limitations." },
  { num: 60, sector: "HEALTH", name: "Genetic Muscle Type Profiling", desc: "Understanding percentage of fast-twitch vs. slow-twitch fibers to tailor conditioning." },

  // Sector 4: TRAINING (61 - 80)
  { num: 61, sector: "TRAINING", name: "Plyometric Speed & Power Metrics", desc: "Assessing explosive vertical jump heights and ground contact times using force plates." },
  { num: 62, sector: "TRAINING", name: "Aerobic Vo2Max Interval Builders", desc: "Configuring high-intensity interval runs (HIIT) to push anaerobic threshold boundaries." },
  { num: 63, sector: "TRAINING", name: "Tactical Set-Piece Simulator", desc: "interactive chalkboard to map corner kick movements, blocker screens, and wall defense setups." },
  { num: 64, sector: "TRAINING", name: "Biomechanical Joint Kinematics", desc: "Angle calculations of the hip and knee joints during maximum velocity sprints." },
  { num: 65, sector: "TRAINING", name: "Team Positioning Heatmap Sync", desc: "Compiling GPS vest spatial coordinates to audit structural compact zones on the pitch." },
  { num: 66, sector: "TRAINING", name: "Velocity-Based Weight Protocols", desc: "Measuring bar speed in meters per second to customize optimal daily lifting loads." },
  { num: 67, sector: "TRAINING", name: "Agility Drill Interval Timers", desc: "Optimizing sprint changes of direction with custom shuttle run schemas and light triggers." },
  { num: 68, sector: "TRAINING", name: "High-definition Video Analysis", desc: "Tagging tactical errors, transition movements, and counter-pressing reactions on raw game films." },
  { num: 69, sector: "TRAINING", name: "Neuromuscular Reactivity Drills", desc: "Cognitive split-second decision drills triggered by color-changing visual markers." },
  { num: 70, sector: "TRAINING", name: "Virtual Reality Tactical Drills", desc: "Immersive 3D play recognition training for quarterbacks, midfielders, and defenders." },
  { num: 71, sector: "TRAINING", name: "Resistance Bands Load Metrics", desc: "Variable load training curves designed to increase power output in terminal acceleration phases." },
  { num: 72, sector: "TRAINING", name: "Position-Specific Skill Trackers", desc: "Tracking metrics specialized for strikers (shot accuracy), wingers (cross count), and defenders (tackles)." },
  { num: 73, sector: "TRAINING", name: "Altitude Training Room Clones", desc: "Hypoxic room configuration variables to stimulate EPO production and oxygen efficiency." },
  { num: 74, sector: "TRAINING", name: "Core Abdominals Strength Charts", desc: "Plotting structural stability parameters to maximize force transfers during rotation movements." },
  { num: 75, sector: "TRAINING", name: "Weighted Vest Jump Estimators", desc: "Developing eccentric strength curves using progressive vest load jumps." },
  { num: 76, sector: "TRAINING", name: "Reactive Agility Light Systems", desc: "Reviewing sensor delays to optimize athlete reactions to lateral targets." },
  { num: 77, sector: "TRAINING", name: "Pitch Friction Surface Coefficient", desc: "Auditing grass moisture and turf friction to select optimal shoe stud length." },
  { num: 78, sector: "TRAINING", name: "Football Pitch Telemetry Integrator", desc: "Connecting local stadium antenna nodes to compile real-time spatial positioning metrics." },
  { num: 79, sector: "TRAINING", name: "Drone Aerological Videography", desc: "Processing tactical high-angle footage to track collective team movement geometries." },
  { num: 80, sector: "TRAINING", name: "High-performance Sports Lab Benchmarks", desc: "Benchmarking athlete stats against elite global sports database guidelines." },

  // Sector 5: DIETS (81 - 100)
  { num: 81, sector: "DIETS", name: "High-Protein Muscle Synthesis Menu", desc: "Designing muscle repair meal preps balancing organic chicken breast, grass-fed beef, and egg whites." },
  { num: 82, sector: "DIETS", name: "Macronutrient Caloric Split Calculator", desc: "Dynamic calculator tailoring daily intake based on sport type, body weight, and workout volume." },
  { num: 83, sector: "DIETS", name: "Pre-Match Carb Loading Schedule", desc: "Structuring glycogen accumulation sheets over a 48-hour build-up phase to avoid hitting the wall." },
  { num: 84, sector: "DIETS", name: "Anti-Inflammatory Turmeric Shake", desc: "Diet recipes rich in ginger, turmeric, and high-quality fats to reduce systemic soft tissue soreness." },
  { num: 85, sector: "DIETS", name: "Liquid Mineral and Ketone Protocols", desc: "Deploying exogenous ketones and clean salt solutions during long duration training cycles." },
  { num: 86, sector: "DIETS", name: "Customized Sweat Rate Rehydration", desc: "Compiling precise magnesium, potassium, and sodium balance ratios per liter of water." },
  { num: 87, sector: "DIETS", name: "High-density Ketogenic Fueling Files", desc: "Fat-adapted energy schemas for athletes focusing on extreme long distance aerobic running." },
  { num: 88, sector: "DIETS", name: "Collagen PepSecurePayment Joint Support Diets", desc: "Integrating collagen and Vitamin C precisely 40 minutes before workouts to strengthen tendons." },
  { num: 89, sector: "DIETS", name: "Sports SuSecure Invoice Register NSF Certified Lists", desc: "Auditing creatine, amino acids, and multivitamins to ensure absolute third-party batch safety." },
  { num: 90, sector: "DIETS", name: "Post-Training Insulin Response Meals", desc: "Speeding up glycogen transport through high glycemic carbohydrates paired with clean protein." },
  { num: 91, sector: "DIETS", name: "Metabolic Fat Oxidation Indexes", desc: "Evaluating fat burning efficiency at varying heart rate zones to adjust dietary fat splits." },
  { num: 92, sector: "DIETS", name: "Micronutrient Serum Mineral Balances", desc: "Tracking zinc, iron, magnesium, and calcium serum markers to combat deficiencies." },
  { num: 93, sector: "DIETS", name: "Hypocaloric Fighter Weight Cutting", desc: "Structured water shunting and high-protein calorie deficit templates for weigh-in milestones." },
  { num: 94, sector: "DIETS", name: "Off-season Vegan Power Diet Plan", desc: "High-integrity plant-based fuel guides utilizing peas, lentils, quinoa, and seed combinations." },
  { num: 95, sector: "DIETS", name: "Intermittent Fasting Performance Logs", desc: "Managing performance markers during restricted diet periods to maintain muscular mass." },
  { num: 96, sector: "DIETS", name: "Match-Day Energy Gel Logistics", desc: "Scheduling dynamic carbohydrate intakes every 30 minutes during competitive matches." },
  { num: 97, sector: "DIETS", name: "Vitamin D3 Receptor Tuning Sheets", desc: "Optimizing testosterone production and calcium absorption with exact fat-soluble dosages." },
  { num: 98, sector: "DIETS", name: "Cold-pressed Beetroot Nitrate Infusion", desc: "Maximizing vascular expansion and stamina through beetroot juice cycles before kick-off." },
  { num: 99, sector: "DIETS", name: "Raw Milk Superfood Shake Formulas", desc: "Traditional nutrition recovery shakes combining whole milk, raw honey, and high-integrity eggs." },
  { num: 100, sector: "DIETS", name: "Anti-Bloat Gut Microbiome Meals", desc: "Optimizing nutrient absorption using fermented foods, prebiotics, and low-FODMAP diet configurations." }
];

// Generate exactly 100 sub-categories list
export const CATEGORY_112_SUBCATEGORIES = SUB_CAT_TEMPLATES.map(t => ({
  id: `CAT-112.${t.num.toString().padStart(2, '0')}`,
  categoryNumber: 112,
  subNumber: t.num,
  sectorId: t.sector,
  name: t.name,
  desc: t.desc,
  defaultPrompt: `Audit and optimize Sports Category 112 Node properties for ${t.name}. Initialize 0.8s validation routines, routing 1% 3% Community Allocation and 10% Maintenance & Hosting Fee to Secure Payment Provider (Verified Payment Configuration | Secure Billing Provider).`
}));

export function NexoraXSportsCategoryConsole() {
  const [selectedSector, setSelectedSector] = useState<string>('ALL');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [activeSubNode, setActiveSubNode] = useState(CATEGORY_112_SUBCATEGORIES[0]);
  const [promptPayload, setPromptPayload] = useState(CATEGORY_112_SUBCATEGORIES[0].defaultPrompt);
  
  // Dynamic form states
  const [grossRevenueInput, setGrossRevenueInput] = useState<number>(250000);
  const [athleteWeight, setAthleteWeight] = useState<number>(80);
  const [selectedSport, setSelectedSport] = useState<string>('FOOTBALL');
  const [calcResults, setCalcResults] = useState<any>(null);
  const [dietResults, setDietResults] = useState<any>(null);

  // Scribe State
  const [athleteName, setAthleteName] = useState('Helena Smith');
  const [athleteClub, setAthleteClub] = useState('Dunfermline Athletic Elite');
  const [isSecurePaymentProviderOffsetActive, setIsSecurePaymentProviderOffsetActive] = useState(true);
  const [draftResult, setDraftResult] = useState<string>('');
  
  // Execution Console State
  const [isExecuting, setIsExecuting] = useState(false);
  const [logs, setLogs] = useState<string[]>([
    "SPORTS CORE INTEGRATION NODE-112 INITIALIZED.",
    "MANDATORY PROTOCOL SECURED: READY FOR SPORTING DATA EXTRACTION."
  ]);
  const [syncStatus, setSyncStatus] = useState<'IDLE' | 'SYNCING' | 'SECURED'>('IDLE');

  // Trigger Calculations on startup / change
  useEffect(() => {
    executeBusinessCalculation();
  }, [grossRevenueInput]);

  useEffect(() => {
    executeDietCalculation();
  }, [athleteWeight, selectedSport]);

  useEffect(() => {
    setPromptPayload(activeSubNode.defaultPrompt);
  }, [activeSubNode]);

  // Tax and levy accounting calculation matching AGENTS.md
  const executeBusinessCalculation = () => {
    const amount = Number(grossRevenueInput) || 0;
    const levy = amount * 0.01;
    const taxValue = (amount - levy) * 0.20;
    const netSecurePayment = amount - levy - taxValue;
    const charity1 = levy / 3;
    const charity2 = levy / 3;
    const charity3 = levy / 3;

    setCalcResults({
      gross: amount,
      levy: levy,
      tax: taxValue,
      net: netSecurePayment,
      charities: {
        charity1: charity1,
        charity2: charity2,
        charity3: charity3
      }
    });
  };

  // Nutrition dynamic macronutrient planner
  const executeDietCalculation = () => {
    const weight = Number(athleteWeight) || 75;
    let proteinMultiplier = 2.0; // g per kg
    let carbMultiplier = 4.0;
    let fatMultiplier = 1.0;
    let baseCalories = weight * 35;

    if (selectedSport === 'POWERLIFTING') {
      proteinMultiplier = 2.4;
      carbMultiplier = 3.5;
      fatMultiplier = 1.2;
      baseCalories = weight * 42;
    } else if (selectedSport === 'MARATHON') {
      proteinMultiplier = 1.6;
      carbMultiplier = 7.0;
      fatMultiplier = 0.9;
      baseCalories = weight * 48;
    } else if (selectedSport === 'COMBAT_SPORTS') {
      proteinMultiplier = 2.2;
      carbMultiplier = 3.0;
      fatMultiplier = 1.0;
      baseCalories = weight * 32;
    } else if (selectedSport === 'RUGBY') {
      proteinMultiplier = 2.2;
      carbMultiplier = 5.0;
      fatMultiplier = 1.1;
      baseCalories = weight * 45;
    }

    const proteinG = Math.round(weight * proteinMultiplier);
    const carbsG = Math.round(weight * carbMultiplier);
    const fatG = Math.round(weight * fatMultiplier);
    const waterL = (weight * 0.045).toFixed(1);

    setDietResults({
      calories: baseCalories,
      protein: proteinG,
      carbs: carbsG,
      fats: fatG,
      water: waterL,
      recommendedMeal: selectedSport === 'POWERLIFTING' ? '300g Grass-Fed Beef Steak, 400g Sweet Potatoes, and Asparagus.' :
                        selectedSport === 'MARATHON' ? '200g Grilled Salmon Phillet, 250g Brown Pasta, and Avocado slices.' :
                        selectedSport === 'COMBAT_SPORTS' ? '250g Organic Chicken breast, Jasmine rice, Broccoli, and Lemon water.' :
                        '350g Roasted Turkey breast, baked Potatoes, and raw Spinach salad with honey dressing.'
    });
  };

  // Scribe template generation with NexoraX billing records NI offset rules
  const handleDraftContract = () => {
    const draft = `**NEXORAX ATHLETIC PROFESSIONAL COVENANT (CAT-112)**\n\n**STATUS:** REGISTERED & CRYPTOGRAPHICALLY SECURED\n**LICENSOR:** NexoraX Digital Ltd (SC887095) under authorization from the Office of the Director, Lead Director.\n\n---\n\n### COVENANT TARGET: ${athleteClub} (${athleteName})\n**DATE:** ${new Date().toLocaleDateString()}\n**SUBJECT:** Elite Professional Agreement & NexoraX billing records Offset\n\n**1. INSTITUTIONAL ENGAGEMENT**\nThis document outlines the professional deployment parameters of ${athleteName} to optimize cardiovascular recovery, tactical gameplay matrices, and high-velocity metabolic throughput under Category 112 directive limits.\n\n**2. REMUNERATION & Secure Payment Provider OFFSET (NI: Secure Credential Stored Server-Side)**\nIn compliance with non-negotiable financial mandates, all administrative support, physical therapists, and performance equipment adjustments are offset up to 100% via the NexoraX billing records program, registering an zero-cost baseline to the sports club.\n\n**3. 3% Community Allocation MATRIX**\nAll transfer settlements, sponsorship retainers, and branding commissions route as follows:\n- 1% 3% Community Allocation routed cleanly to Secure Payment Provider (Sort Code: Verified Payment Configuration | Account: Secure Billing Provider) with strict 33.33% splits to Dunfermline Local, UK national, and Global Foundations.\n- 10% Maintenance & Hosting Fee on the net remainder.\n- 79% Net settling directly to the primary Secure Invoice Register.\n\n**4. CONFIDENTIALITY PROTOCOLS (0.8s LATENCY)**\nBoth parties execute with perfect discretion. Zero copy/screenshot protocol is strictly operational. Managed under CloudSync-02 telemetry active observation.\n\n**Lead Director**\nDirector, NexoraX Digital Ltd\n\n**Agreed by Athlete / Representative:** ________________________`;

    setDraftResult(draft);
    setLogs(prev => [...prev, `CONTRACT DRAFTED FOR ${athleteName} AT ${athleteClub}.`]);
  };

  const handleExecuteAlgorithm = () => {
    setIsExecuting(true);
    setLogs(prev => [...prev, `LAUNCHING COGNITIVE SCAN: ${activeSubNode.id}...`]);
    
    setTimeout(() => {
      setIsExecuting(false);
      setLogs(prev => [
        ...prev,
        `[SUCCESS] 0.8s Latency check passed. Results validated.`,
        `[ACCOUNING] Extracted Splits: 3% Community Allocation: £${(grossRevenueInput * 0.01).toFixed(2)} | 10% Maintenance & Hosting Fee: £${((grossRevenueInput - (grossRevenueInput * 0.01)) * 0.20).toFixed(2)}`,
        `[REGISTRY] Ledger entry cryptographically mirrored to Google Drive NexoraX Resource Library.`
      ]);
    }, 800);
  };

  const handleSyncToDrive = () => {
    setSyncStatus('SYNCING');
    setLogs(prev => [...prev, `INITIALIZING CLOUD PORTAL SHUTTLE VIA CloudSync-02...`]);
    
    setTimeout(() => {
      setSyncStatus('SECURED');
      setLogs(prev => [...prev, `[NexoraX Resource Library] Sports Category 112 data packets sealed in Drive.`]);
    }, 1200);
  };

  // Filter 100 sub-categories
  const filteredSubNodes = CATEGORY_112_SUBCATEGORIES.filter(node => {
    const matchesSector = selectedSector === 'ALL' || node.sectorId === selectedSector;
    const matchesSearch = node.name.toLowerCase().includes(searchQuery.toLowerCase()) || 
                          node.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          node.desc.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSector && matchesSearch;
  });

  return (
    <div id="sports-category-console" className="w-full text-zinc-100 bg-black/60 border border-zinc-800 rounded-lg p-6 max-w-7xl mx-auto shadow-2xl relative">
      
      {/* 1. Arabic Praise Header (Mandatory Rule) */}
      <div className="text-center border-b border-zinc-800 pb-5 mb-6">
        <p className="text-[10px] md:text-xs font-serif text-amber-500 italic uppercase tracking-widest">{VERIFIED_PRAISE}</p>
        <span className="text-zinc-500 text-[9px] font-mono tracking-wider">NEXORAX NEXORAX COMMUNICATIONS NODE • CATEGORY 112 COMMAND</span>
        <h1 className="text-2xl font-bold text-white tracking-tight flex items-center justify-center gap-2 mt-2">
          <Trophy className="w-6 h-6 text-amber-500 animate-pulse" />
          CATEGORY 112: SPORTS & ATHLETICS MANAGEMENT
        </h1>
        <p className="text-zinc-400 text-xs max-w-2xl mx-auto mt-2">
          High-velocity corporate console managing 100 sub-categories of professional sportsman contracts, club business revenue routing, player recovery telemetry, and elite performance diet macros.
        </p>
      </div>

      {/* Grid Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        
        {/* Left Column: 100 Sub-Categories directory with search and filter selector (Grid 5) */}
        <div className="lg:col-span-5 flex flex-col gap-4 border-r border-zinc-900 pr-0 lg:pr-6">
          <div className="flex flex-col gap-2">
            <label className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">Sector Filter System</label>
            <div className="flex flex-wrap gap-1">
              <button 
                onClick={() => setSelectedSector('ALL')}
                className={`px-2 py-1 rounded text-[9px] uppercase tracking-wider font-semibold border transition-all ${selectedSector === 'ALL' ? 'bg-amber-500/20 border-amber-500 text-amber-400' : 'bg-zinc-900 border-zinc-805 text-zinc-400 hover:border-zinc-700'}`}
              >
                All (100)
              </button>
              {SPORTS_SECTORS.map(s => (
                <button 
                  key={s.id}
                  onClick={() => setSelectedSector(s.id)}
                  className={`px-2 py-1 rounded text-[9px] uppercase tracking-wider font-semibold border transition-all ${selectedSector === s.id ? 'bg-amber-500/20 border-amber-500 text-amber-400' : 'bg-zinc-900 border-zinc-805 text-zinc-400 hover:border-zinc-700'}`}
                >
                  {s.id}
                </button>
              ))}
            </div>
          </div>

          <div className="relative">
            <Search className="absolute left-3 top-2.5 w-3.5 h-3.5 text-zinc-500" />
            <input 
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search 100 active sub-categories..."
              className="w-full bg-zinc-950 border border-zinc-850 rounded px-9 py-2 text-xs text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500"
            />
          </div>

          {/* Subnodes Scrollbox */}
          <div className="flex flex-col gap-1.5 max-h-[480px] overflow-y-auto pr-2 scrollbar-thin">
            {filteredSubNodes.length === 0 ? (
              <p className="text-zinc-600 text-xs text-center py-6 italic">No sub-categories matched criteria.</p>
            ) : (
              filteredSubNodes.map(node => {
                const isSelected = activeSubNode.id === node.id;
                return (
                  <button
                    key={node.id}
                    onClick={() => setActiveSubNode(node)}
                    className={`text-left p-2.5 rounded border transition-all flex flex-col gap-1 ${isSelected ? 'bg-zinc-900/80 border-amber-500' : 'bg-zinc-950/40 border-zinc-900 hover:border-zinc-800'}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] font-mono text-amber-500 font-bold">{node.id}</span>
                      <span className="text-[8px] font-mono text-zinc-600 uppercase tracking-widest">{node.sectorId}</span>
                    </div>
                    <h3 className="text-xs text-zinc-100 font-semibold">{node.name}</h3>
                    <p className="text-[10px] text-zinc-500 line-clamp-1">{node.desc}</p>
                  </button>
                );
              })
            )}
          </div>
        </div>

        {/* Right Column: Active Interactive Workspaces (Grid 7) */}
        <div className="lg:col-span-7 flex flex-col gap-6">
          
          {/* Active Sub-category Detail Cards */}
          <div className="p-4 bg-zinc-950/80 border border-zinc-850 rounded-lg flex flex-col gap-3 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-amber-500/5 rounded-full blur-2xl pointer-events-none" />
            
            <div className="flex items-center justify-between border-b border-zinc-900 pb-2">
              <div className="flex items-center gap-2">
                <span className="bg-amber-500/10 text-amber-400 font-mono text-[10px] px-2 py-0.5 rounded border border-amber-500/20">{activeSubNode.id}</span>
                <span className="text-zinc-400 text-xs uppercase font-mono tracking-widest">Active Sub-Category Block</span>
              </div>
              <div className="flex items-center gap-1.5 text-[10px] text-emerald-400">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                0.8s SAFE
              </div>
            </div>

            <div>
              <h2 className="text-lg font-bold text-white tracking-tight">{activeSubNode.name}</h2>
              <p className="text-xs text-zinc-400 mt-1">{activeSubNode.desc}</p>
            </div>

            {/* Prompt Editor */}
            <div className="flex flex-col gap-1.5 bg-black/40 border border-zinc-900 p-2.5 rounded">
              <div className="flex justify-between items-center">
                <label className="text-[9px] font-mono text-zinc-500 uppercase tracking-widest">Cognitive Prompt Payload</label>
                <div className="flex items-center gap-1.5 text-[9px] text-[#e840f3] font-mono">
                  <Sparkles className="w-2.5 h-2.5" />
                  GEN AI READY
                </div>
              </div>
              <textarea
                value={promptPayload}
                onChange={(e) => setPromptPayload(e.target.value)}
                className="w-full bg-transparent border-0 text-[11px] font-mono text-zinc-300 resize-none h-14 focus:ring-0 focus:outline-none placeholder-zinc-700"
              />
            </div>

            <div className="flex items-center gap-2 pt-1">
              <button 
                onClick={handleExecuteAlgorithm}
                disabled={isExecuting}
                className="bg-amber-500 hover:bg-amber-600 disabled:bg-zinc-700 text-black px-4 py-1.5 rounded text-xs font-bold uppercase tracking-wider flex items-center gap-1.5 transition-all"
              >
                {isExecuting ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                Execute Cognitive Audit
              </button>
              <button 
                onClick={handleSyncToDrive}
                className="bg-zinc-900 border border-zinc-800 hover:border-zinc-700 text-zinc-300 px-4 py-1.5 rounded text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5 transition-all"
              >
                <Save className="w-3.5 h-3.5" />
                {syncStatus === 'SYNCING' ? 'Syncing...' : syncStatus === 'SECURED' ? 'Drive Secured' : 'Sync to NexoraX Resource Library'}
              </button>
            </div>
          </div>

          {/* Interactive Block A: Sports Business Splits Calculator */}
          <div className="p-4 bg-zinc-950/40 border border-zinc-900 rounded-lg flex flex-col gap-4">
            <h3 className="text-xs font-bold text-white tracking-widest font-mono uppercase flex items-center gap-2 border-b border-zinc-900 pb-2">
              <Calculator className="w-4 h-4 text-emerald-500" />
              NexoraX Club Revenue & Levy Calculator
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
              <div className="sm:col-span-6 flex flex-col gap-1.5">
                <label className="text-[10px] font-mono text-zinc-500 uppercase">Gross Club Transfer/Sponsorship (£)</label>
                <input 
                  type="number"
                  value={grossRevenueInput}
                  onChange={(e) => setGrossRevenueInput(Math.max(0, parseFloat(e.target.value) || 0))}
                  placeholder="E.G., 500000"
                  className="bg-zinc-950 border border-zinc-850 rounded px-3 py-1.5 text-xs text-white placeholder-zinc-700 focus:outline-none focus:border-amber-500"
                />
              </div>
              <div className="sm:col-span-6 text-zinc-500 text-[10px] flex flex-col justify-center leading-relaxed font-mono">
                <p>⚠️ **MANDATORY FISCAL RULES:**</p>
                <p>All receipts are audited for the **1% 3% Community Allocation** and subsequent **20% Gavel 10% Maintenance & Hosting Fee**, routing directly to **Secure Payment Provider**.</p>
              </div>
            </div>

            {calcResults && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-center">
                <div className="bg-zinc-950/80 border border-zinc-900 p-2.5 rounded">
                  <div className="text-[10px] font-mono text-zinc-500">1% 3% Community Allocation</div>
                  <div className="text-sm font-black text-amber-400 mt-1">£{calcResults.levy.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  <div className="text-[8px] font-mono text-zinc-600 mt-1">Splits: £{(calcResults.charities.charity1).toFixed(2)} x3</div>
                </div>
                <div className="bg-zinc-950/80 border border-zinc-900 p-2.5 rounded">
                  <div className="text-[10px] font-mono text-zinc-500">10% Maintenance & Hosting Fee</div>
                  <div className="text-sm font-black text-red-400 mt-1">£{calcResults.tax.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  <div className="text-[8px] font-mono text-zinc-600 mt-1">NexoraX Gavel allocation</div>
                </div>
                <div className="bg-zinc-950/80 border border-zinc-900 p-2.5 rounded border-emerald-500/20">
                  <div className="text-[10px] font-mono text-zinc-500">NET SecurePayment SETTLEMENT</div>
                  <div className="text-sm font-black text-emerald-400 mt-1">£{calcResults.net.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  <div className="text-[8px] font-mono text-emerald-500/60 mt-1">Route to: Verified Payment Configuration | Secure Billing Provider</div>
                </div>
              </div>
            )}
          </div>

          {/* Interactive Block B: Diet and Macronutrient Planner */}
          <div className="p-4 bg-zinc-950/40 border border-zinc-900 rounded-lg flex flex-col gap-4">
            <h3 className="text-xs font-bold text-white tracking-widest font-mono uppercase flex items-center gap-2 border-b border-zinc-900 pb-2">
              <Apple className="w-4 h-4 text-amber-500" />
              Athlete Sport Specific Macronutrient Diet Planner
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-4">
              <div className="sm:col-span-4 flex flex-col gap-1.5">
                <label className="text-[10px] font-mono text-zinc-500 uppercase">Weight (kg)</label>
                <input 
                  type="number"
                  value={athleteWeight}
                  onChange={(e) => setAthleteWeight(Math.max(30, parseFloat(e.target.value) || 0))}
                  className="bg-zinc-950 border border-zinc-850 rounded px-3 py-1.5 text-xs text-white placeholder-zinc-700 focus:outline-none focus:border-amber-500"
                />
              </div>
              <div className="sm:col-span-8 flex flex-col gap-1.5">
                <label className="text-[10px] font-mono text-zinc-500 uppercase">Target Sport Discipline</label>
                <select 
                  value={selectedSport}
                  onChange={(e) => setSelectedSport(e.target.value)}
                  className="bg-zinc-950 border border-zinc-850 rounded px-3 py-1.5 text-xs text-white focus:outline-none focus:border-amber-500"
                >
                  <option value="FOOTBALL">Football / Athletic Performance (HIIT)</option>
                  <option value="POWERLIFTING">Powerlifting / Strength Hypertrophy</option>
                  <option value="MARATHON">Marathon / Extreme Aerobic Endurance</option>
                  <option value="COMBAT_SPORTS">Combat Sports / Fast Weight Cut</option>
                  <option value="RUGBY">Rugby / Collision Impact Sport</option>
                </select>
              </div>
            </div>

            {dietResults && (
              <div className="p-3 bg-zinc-950 border border-zinc-900 rounded flex flex-col gap-2">
                <div className="grid grid-cols-4 gap-2 text-center border-b border-zinc-900 pb-2">
                  <div>
                    <div className="text-[8px] font-mono text-zinc-500 uppercase">Calories</div>
                    <div className="text-xs font-bold text-zinc-200">{dietResults.calories} kcal</div>
                  </div>
                  <div>
                    <div className="text-[8px] font-mono text-zinc-500 uppercase">Protein</div>
                    <div className="text-xs font-bold text-amber-400">{dietResults.protein}g</div>
                  </div>
                  <div>
                    <div className="text-[8px] font-mono text-zinc-500 uppercase">Carbohydrates</div>
                    <div className="text-xs font-bold text-cyan-400">{dietResults.carbs}g</div>
                  </div>
                  <div>
                    <div className="text-[8px] font-mono text-zinc-500 uppercase">Fats</div>
                    <div className="text-xs font-bold text-[#e840f3]">{dietResults.fats}g</div>
                  </div>
                </div>
                <div className="flex gap-2 items-start mt-1 text-[11px] leading-relaxed text-zinc-300">
                  <Coffee className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <span className="font-mono text-[9px] text-zinc-500 block uppercase font-bold">Recommended Performance Meal Protocol</span>
                    {dietResults.recommendedMeal} Take with {dietResults.water} liters of trace mineralized water continuously.
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Interactive Block C: Sports Talent Covenant Scribe */}
          <div className="p-4 bg-zinc-950/40 border border-zinc-900 rounded-lg flex flex-col gap-4">
            <h3 className="text-xs font-bold text-white tracking-widest font-mono uppercase flex items-center gap-2 border-b border-zinc-900 pb-2">
              <Clipboard className="w-4 h-4 text-[#e840f3]" />
              NexoraX Athlete Talent Covenant Scribe
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-mono text-zinc-500 uppercase">Athlete Full Name</label>
                <input 
                  type="text"
                  value={athleteName}
                  onChange={(e) => setAthleteName(e.target.value)}
                  placeholder="Helena Smith"
                  className="bg-zinc-950 border border-zinc-850 rounded px-3 py-1.5 text-xs text-white placeholder-zinc-700"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-mono text-zinc-500 uppercase">Sports Club / Team</label>
                <input 
                  type="text"
                  value={athleteClub}
                  onChange={(e) => setAthleteClub(e.target.value)}
                  placeholder="Dunfermline Athletic"
                  className="bg-zinc-950 border border-zinc-850 rounded px-3 py-1.5 text-xs text-white placeholder-zinc-700"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <input 
                type="checkbox"
                id="Secure Payment Provider-offset-checkbox"
                checked={isSecurePaymentProviderOffsetActive}
                onChange={(e) => setIsSecurePaymentProviderOffsetActive(e.target.checked)}
                className="rounded bg-zinc-950 border-zinc-850 text-amber-500 focus:ring-0"
              />
              <label htmlFor="Secure Payment Provider-offset-checkbox" className="text-[10px] font-mono text-zinc-400 cursor-pointer">
                Include NexoraX billing records subsidy clauses (NI: Secure Credential Stored Server-Side offset)
              </label>
            </div>

            <button 
              onClick={handleDraftContract}
              className="w-full bg-[#e840f3]/20 hover:bg-[#e840f3]/35 text-[#e840f3] border border-[#e840f3]/40 py-1.5 rounded text-xs font-bold uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all"
            >
              <FileText className="w-4 h-4" />
              Scribe NexoraX Athlete Covenant
            </button>

            {draftResult && (
              <div className="p-3 bg-zinc-950 border border-zinc-900 rounded max-h-[180px] overflow-y-auto">
                <pre className="text-[9px] font-mono whitespace-pre-wrap text-zinc-300 leading-relaxed tabular-nums">
                  {draftResult}
                </pre>
              </div>
            )}
          </div>

          {/* Terminal Console Activity Monitor */}
          <div className="bg-zinc-950 border border-zinc-900 rounded p-3 flex flex-col gap-2">
            <div className="flex items-center justify-between border-b border-zinc-900 pb-1.5">
              <span className="text-[9px] font-mono text-zinc-500 uppercase tracking-widest">System Signal Logs</span>
              <span className="bg-amber-500/10 text-amber-400 text-[8px] font-mono px-1.5 py-0.5 rounded uppercase font-bold text-right">TELEMETRY SYSTEM MONITOR</span>
            </div>
            <div className="max-h-[100px] overflow-y-auto flex flex-col gap-1 font-mono text-[9px] text-zinc-400">
              {logs.map((log, index) => (
                <div key={index} className="flex gap-1">
                  <span className="text-zinc-600 font-bold whitespace-nowrap">[{new Date().toLocaleTimeString()}]</span>
                  <span className={`${log.startsWith('[SUCCESS]') ? 'text-emerald-400' : log.startsWith('[ACCOUNING]') ? 'text-[#e840f3]' : log.startsWith('MANDATORY') ? 'text-amber-400' : 'text-zinc-300'}`}>{log}</span>
                </div>
              ))}
            </div>
          </div>

        </div>

      </div>

    </div>
  );
}
